import { COMPLETE } from '@/constants/action';
import { useBlocks } from '@/services/block/hooks';
import { useBlockEvents } from '@/services/blockEvent/hooks';
import { useCategories } from '@/services/categories/hooks';
import { useCategoryEvents } from '@/services/categoryEvent/hooks';
import { useCronofyProfiles } from '@/services/cronofy/hooks';
import { useExternalEvents } from '@/services/events/hooks';
import { useDailyPlanActionEvents } from '@/services/myPlan/hooks';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { getDurationInMinutes } from '@/utils/action';
import {
  blockEventAdapterApi,
  categoryEventAdapterApi,
  checkIfIsAllDay,
  endOfTheWeek,
  externalCalendarMyEventAdapterApi,
  formatDateToString,
  generateInstances,
  isPastWeek,
  startOfTheWeek,
  utcToLocalDate,
} from '@/utils/calendar';
import { useMapBy } from '@/utils/useMapBy';
import {
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  getDate,
  getMonth,
  isAfter,
  isBefore,
  isSameDay,
  isSameWeek,
  startOfDay,
} from 'date-fns';
import { flatMap } from 'lodash';
import { ReactNode, useCallback, useMemo } from 'react';

import { CalendarContext } from './context';

type CalendarProviderProps = {
  children: ReactNode;
  date: Date;
  view: string;
};

function CalendarProvider({ children, date, view }: CalendarProviderProps) {
  const startDate = date && startOfTheWeek(date);
  const endDate = date && endOfTheWeek(date);

  const { data: weeklyPlanId } = useWeeklyPlanId(date);
  const { data: cronofyProfiles } = useCronofyProfiles();
  const { data: actionEvents } = useDailyPlanActionEvents(weeklyPlanId, date, view);
  const { data: externalEvents } = useExternalEvents(startDate, endDate);
  const { data: categories } = useCategories();
  const { data: blocks } = useBlocks();
  const categoriesMap = useMapBy(categories?.category || [], 'id');
  const blocksMap = useMapBy(blocks?.block || [], 'id');
  const { data: categoryEvents } = useCategoryEvents(startDate, endDate);
  const { data: blockEvents } = useBlockEvents(startDate, endDate);

  const areSameDayOfWeek = (date1: Date, date2: Date) => {
    return date1.getDay() === date2.getDay();
  };

  const makeHeaderDuration = useCallback(
    (day: Date) => {
      let starredEventsDuration = 0;
      let scheduledEventsDuration = 0;
      let plannedEventsDuration = 0;
      let externalEventsDuration = 0;

      function isValidIntervalOccurrence(
        start: Date,
        current: Date,
        interval: number,
        frequency: string,
        endDate?: Date | null,
      ): boolean {
        if (endDate && isAfter(current, endDate)) return false;

        if (interval === 1) return true;

        switch (frequency) {
          case 'daily':
            return differenceInDays(current, start) % interval === 0;
          case 'weekly':
            return differenceInWeeks(current, start) % interval === 0;
          case 'monthly':
            return differenceInMonths(current, start) % interval === 0;
          case 'yearly':
            return differenceInYears(current, start) % interval === 0;
          default:
            return false;
        }
      }

      if (actionEvents) {
        for (const actionEvent of actionEvents) {
          const { action } = actionEvent;

          const isActionNotScheduled =
            (view === 'day' && !action.scheduledTime) || (view === 'week' && !action.scheduledDate);

          if (action.progressStatus === COMPLETE || isActionNotScheduled) {
            continue;
          }

          const fullDate = action.event.start?.split('T')[0];

          const actionDate = utcToLocalDate(
            fullDate || action.scheduledDate || '',
            action.scheduledTime,
            action.timezone,
            action.gmtOffset,
          );

          const { event } = action;

          let customs;
          let actionEndDate = null;

          if (event?.recurrence?.rules?.[0]?.until) {
            actionEndDate = utcToLocalDate(
              event.recurrence.rules[0].until,
              action.scheduledTime,
              action.timezone,
              action.gmtOffset,
            );
          }

          if (event?.recurrence?.rules?.[0]) {
            const eventStart = actionDate;
            const exceptions = (event.recurrence.exceptions?.add || []).map((exception: { date: string }) =>
              utcToLocalDate(exception.date, action.scheduledTime, action.timezone, action.gmtOffset),
            );

            customs = event.recurrence.exceptions?.custom;

            const validDays = event.recurrence?.rules[0]?.by_day;
            const interval = event.recurrence?.rules[0]?.interval || 1;
            const frequency = event.recurrence?.rules[0]?.frequency;
            const count = event.recurrence?.rules[0]?.count;

            if (frequency === 'weekly' && validDays?.length) {
              const weekdayMap: Record<string, number> = {
                sunday: 0,
                monday: 1,
                tuesday: 2,
                wednesday: 3,
                thursday: 4,
                friday: 5,
                saturday: 6,
              };

              const validDayNumbers = validDays.map((d: { day: string }) => weekdayMap[d.day]);
              const dayOfWeek = day.getDay();
              const startDayOfWeek = actionDate.getDay();

              const isCurrentSameWeek = isSameWeek(day, actionDate, { weekStartsOn: 1 });

              const isBeforeStartInSameWeek = isCurrentSameWeek && dayOfWeek < startDayOfWeek;

              if (!validDayNumbers.includes(dayOfWeek) || isBeforeStartInSameWeek) {
                continue;
              }
            }

            if (
              frequency &&
              !isValidIntervalOccurrence(startOfDay(eventStart), startOfDay(day), interval, frequency, actionEndDate)
            ) {
              continue;
            }

            if (count) {
              let occurrences;
              switch (frequency) {
                case 'daily':
                  occurrences = differenceInDays(startOfDay(day), startOfDay(eventStart));
                  break;
                case 'weekly':
                  occurrences = differenceInWeeks(startOfDay(day), startOfDay(eventStart));
                  break;
                case 'monthly':
                  occurrences = differenceInMonths(startOfDay(day), startOfDay(eventStart));
                  break;
                case 'yearly':
                  occurrences = differenceInYears(startOfDay(day), startOfDay(eventStart));
                  break;
                default:
                  occurrences = 0;
              }
              const effectiveOccurrences = Math.floor(occurrences / interval);
              if (effectiveOccurrences >= count) {
                continue;
              }
            }

            if (
              !validDays?.length &&
              frequency === 'weekly' &&
              (!areSameDayOfWeek(day, startOfDay(eventStart)) || isPastWeek(eventStart))
            ) {
              continue;
            }

            if (exceptions.some((exception) => isSameDay(day, exception))) {
              continue;
            }

            if (
              frequency === 'daily' &&
              (isBefore(day, startOfDay(eventStart)) ||
                (actionEndDate && isAfter(day, startOfDay(actionEndDate))) ||
                exceptions.some((exception) => isSameDay(day, exception)))
            ) {
              continue;
            }

            if (frequency === 'monthly' && getDate(day) !== getDate(eventStart)) {
              continue;
            }

            if (
              frequency === 'yearly' &&
              (getMonth(day) !== getMonth(eventStart) || getDate(day) !== getDate(eventStart))
            ) {
              continue;
            }
          }

          if (!isSameDay(day, actionDate) && !event?.recurrence?.rules?.[0]?.frequency) {
            continue;
          }

          if ((customs && customs?.[formatDateToString(day)]?.dateOfStarring) || action.dateOfStarring) {
            starredEventsDuration += getDurationInMinutes(action);
          }

          scheduledEventsDuration += getDurationInMinutes(action);
        }
      }

      if (externalEvents) {
        for (let event of externalEvents) {
          const start = new Date(event.start);
          const end = new Date(event.end);

          const isInternal = actionEvents.find((e) => e.action.title === event.summary);

          if (
            start >= startOfDay(day) &&
            start <= endOfDay(day) &&
            !checkIfIsAllDay(event.start, event.end) &&
            !isInternal
          ) {
            const duration = differenceInMinutes(end, start);
            externalEventsDuration += duration;
          }
        }
      }

      const scheduledEventsWithExternalEventsDuration = scheduledEventsDuration + externalEventsDuration;

      return {
        starredEventsDuration,
        scheduledEventsDuration,
        externalEventsDuration,
        scheduledEventsWithExternalEventsDuration,
        plannedEventsDuration,
      };
    },
    [actionEvents, externalEvents, view],
  );

  const combinedEvents: any = useMemo(() => {
    // Convert external events into Calendar data
    const formattedExternalEvents = externalCalendarMyEventAdapterApi(externalEvents, cronofyProfiles);

    // Convert action events into Calendar data

    const data = actionEvents.map((actionEvent: any) => generateInstances(actionEvent?.action, endDate));

    const formattedTodayActionEvents = flatMap(data);

    const formattedCategoryEvents =
      categoryEvents?.categoryEvent.map((categoryEvent) =>
        categoryEventAdapterApi(categoryEvent, categoriesMap.get(categoryEvent.categoryId)),
      ) ?? [];

    const formattedBlockEvents =
      blockEvents?.blockEvent.map((blockEvent) =>
        blockEventAdapterApi(blockEvent, blocksMap.get(blockEvent.blockId)),
      ) ?? [];

    return [
      ...formattedTodayActionEvents,
      ...formattedCategoryEvents,
      ...formattedBlockEvents,
      ...formattedExternalEvents,
    ];
  }, [
    externalEvents,
    cronofyProfiles,
    actionEvents,
    categoryEvents?.categoryEvent,
    blockEvents?.blockEvent,
    categoriesMap,
    blocksMap,
  ]);

  return (
    <CalendarContext.Provider
      value={{
        actionEvents,
        categoryEvents: categoryEvents?.categoryEvent,
        blockEvents: blockEvents?.blockEvent,
        combinedEvents,
        makeHeaderDuration,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
}

export default CalendarProvider;
